import { FACT_LABEL_FA as FACT_FA } from '@afra/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '../../lib/api'


export function ProfilePage() {
  const queryClient = useQueryClient()
  const [selected, setSelected] = useState<string[] | null>(null)

  const profile = useQuery({ queryKey: ['profile'], queryFn: api.profile })
  const domains = useQuery({ queryKey: ['domains'], queryFn: api.domains })

  const saveDomains = useMutation({
    mutationFn: (codes: string[]) => api.setDomains(codes),
    onSuccess: () => {
      setSelected(null)
      void queryClient.invalidateQueries({ queryKey: ['profile'] })
      // New domains unlock new questions, so the current move may change.
      void queryClient.invalidateQueries({ queryKey: ['journey'] })
    },
  })

  const confirm = useMutation({
    mutationFn: (facts: { code: string; valueFa: string }[]) => api.confirmFacts(facts),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['profile'] }),
  })

  if (profile.isLoading || !profile.data) {
    return (
      <div className="container center">
        <p className="muted">در حال بارگذاری…</p>
      </div>
    )
  }

  const chosen = selected ?? profile.data.domains.map((d) => d.code)
  const inferred = profile.data.facts.filter((f) => f.inferred)

  return (
    <div className="container stack">
      <div className="card">
        <h1>شناسنامه مؤسسه</h1>
        <p className="meta">
          این‌ها را از گفت‌وگویمان برداشت کرده‌ام؛ لازم نیست فرمی پر کنید، فقط تأیید کنید که درست است.
          هیچ‌کدام روی امتیاز بلوغ اثر نمی‌گذارد — کارشان این است که توصیه‌ها به اندازه و کارِ واقعی
          شما بخورد.
        </p>
        {profile.data.sizeBandFa && (
          <p>
            اندازه مؤسسه: <span className="badge badge--good">{profile.data.sizeBandFa}</span>
          </p>
        )}
      </div>

      <div className="card">
        <h2>حوزه فعالیت</h2>
        <p className="meta">
          وقتی بدانم چه کاری می‌کنید، می‌توانم درباره خودِ کار هم بپرسم، نه فقط درباره نحوه اداره‌اش.
          تا پنج حوزه می‌توانید انتخاب کنید.
        </p>
        <div className="options">
          {(domains.data?.domains ?? []).map((d) => {
            const on = chosen.includes(d.code)
            return (
              <button
                key={d.code}
                type="button"
                className={`option ${on ? 'is-selected' : ''}`}
                onClick={() =>
                  setSelected(
                    on ? chosen.filter((c) => c !== d.code) : [...chosen, d.code].slice(0, 5),
                  )
                }
              >
                <span>{d.title_fa}</span>
              </button>
            )
          })}
        </div>
        <button
          type="button"
          className="btn"
          style={{ marginTop: 'var(--space-4)' }}
          disabled={chosen.length === 0 || saveDomains.isPending}
          onClick={() => saveDomains.mutate(chosen)}
        >
          {saveDomains.isPending ? 'در حال ذخیره…' : 'ذخیره حوزه فعالیت'}
        </button>
      </div>

      <div className="card">
        <h2>آنچه تا حالا فهمیده‌ام</h2>
        {profile.data.facts.length === 0 ? (
          <p className="meta">هنوز چیزی ثبت نشده. در گفت‌وگو کم‌کم پر می‌شود.</p>
        ) : (
          <>
            {profile.data.facts.map((f) => (
              <div key={f.code} className="dimension" style={{ gridTemplateColumns: '1fr auto' }}>
                <div>
                  <div className="dimension__title">{FACT_FA[f.code] ?? f.code}</div>
                  <div className="meta">{f.valueFa}</div>
                </div>
                {/* A fact we inferred and a fact they confirmed are different
                    things, and the badge says which — an inferred value can be
                    overwritten by a later turn, a confirmed one cannot. */}
                <span className={`badge ${f.inferred ? 'badge--warn' : 'badge--good'}`}>
                  {!f.inferred ? 'تأیید شما' : f.source === 'legacy_portal' ? 'از پرونده‌تان در پرتال' : 'برداشت من'}
                </span>
              </div>
            ))}
            {inferred.length > 0 && (
              <button
                type="button"
                className="btn btn--ghost"
                style={{ marginTop: 'var(--space-4)' }}
                disabled={confirm.isPending}
                onClick={() =>
                  confirm.mutate(inferred.map((f) => ({ code: f.code, valueFa: f.valueFa })))
                }
              >
                {confirm.isPending ? 'در حال تأیید…' : 'همه درست است، تأیید می‌کنم'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}
